/**
 * Sync logic pour l'inbox.
 *
 * - loadInbox()       : refresh la 1ère page (réinitialise rows)
 * - loadMore()        : append la page suivante
 * - hydrateRow(stub)  : threads.get(METADATA) pour 1 thread → InboxRow
 *
 * Stratégie : threads.list pour les IDs, puis threads.get METADATA en parallèle
 * (max 20 par batch). HTTP/2 multiplexe → ~500ms total pour 20 threads.
 */

import {
  listThreads,
  getThread,
  getHeader,
  parseFrom,
  isThreadUnread,
  modifyThread,
  GmailScopeError,
  type ThreadStub,
} from "./api";
import { gmailState, type InboxRow, type GmailMessage } from "./store.svelte";
import { setBadge } from "../push/badge";
import { showToast } from "../snackbar/store.svelte";
import { loadCache, saveCache } from "./inboxCache";
import { ingestContact, parseAddressList } from "./contacts.svelte";

const PAGE_SIZE = 20;
/** Cap d'auto-pagination par filtre. INBOX peut aller plus loin (boîte
    perso typique 100-300 mails) ; "All mail"/Sent/labels custom plafonnent
    plus bas pour ne pas exploser sur des grands volumes. */
const AUTO_LOAD_CAP_INBOX = 200;
const AUTO_LOAD_CAP_OTHER = 100;
/** Stale threshold : si le cache a moins de cette durée, on n'appelle
    même pas l'API au boot. Au-delà, fetch en background pour freshness. */
const CACHE_FRESH_MS = 60_000;

async function hydrateRow(stub: ThreadStub): Promise<InboxRow> {
  // On fetch aussi "To" (et "Cc") en plus de From/Subject/Date pour
  // alimenter l'index des contacts (autocomplete `from:` / `to:` dans
  // le SearchBar). Coût : ~3 unités/header en plus = négligeable.
  const thread = await getThread(stub.id, {
    format: "metadata",
    metadataHeaders: ["From", "To", "Cc", "Subject", "Date"],
  });

  // Ingest des contacts depuis tous les messages du thread (pas juste
  // le dernier) — donne un index plus riche pour l'autocomplete.
  for (const m of thread.messages) {
    const ts = parseInt(m.internalDate, 10);
    const fromHeader = getHeader(m, "From");
    if (fromHeader) {
      const { name, email } = parseFrom(fromHeader);
      if (email) ingestContact(email, name, ts);
    }
    for (const headerName of ["To", "Cc"] as const) {
      const raw = getHeader(m, headerName);
      if (raw) {
        for (const item of parseAddressList(raw)) {
          ingestContact(item.email, item.name, ts);
        }
      }
    }
  }

  // On veut les headers du DERNIER message (le plus récent dans le thread).
  const last = thread.messages[thread.messages.length - 1];
  const fromRaw = getHeader(last, "From");
  const subject = getHeader(last, "Subject") ?? "(sans objet)";
  const { name, email } = parseFrom(fromRaw);

  return {
    thread,
    fromName: name,
    fromEmail: email,
    subject,
    snippet: thread.snippet ?? "",
    lastDate: parseInt(last.internalDate, 10),
    unread: isThreadUnread(thread),
  };
}

/**
 * Mappe le `kind` du filter sémantique (Inbox/All/Sent/Unread) sur des
 * labels Gmail standards. `labelIds` du filter override ce mapping si
 * non-["INBOX"] (= cas où l'user a tap un split `[NaoMail]/*`).
 */
function resolveLabelIds(): string[] {
  const f = gmailState.filter;
  // Si l'user a sélectionné un split (autre que INBOX), on prend ses labels
  const customLabel = f.labelIds.find((id) => id !== "INBOX");
  if (customLabel) return f.labelIds;
  // Sinon mapping sémantique
  switch (f.kind) {
    case "inbox":  return ["INBOX"];
    case "all":    return []; // pas de filter label
    case "sent":   return ["SENT"];
    case "unread": return ["UNREAD"];
  }
}

async function fetchAndHydrate(opts: {
  pageToken?: string;
}): Promise<{ rows: InboxRow[]; nextPageToken: string | null }> {
  const labelIds = resolveLabelIds();
  const list = await listThreads({
    labelIds,
    query: gmailState.filter.query ?? undefined,
    maxResults: PAGE_SIZE,
    pageToken: opts.pageToken,
  });

  const stubs = list.threads ?? [];
  if (stubs.length === 0) {
    return { rows: [], nextPageToken: null };
  }

  // Parallel hydratation. Promise.all OK ici car on assume succès — la moindre
  // erreur fait tomber tout le batch (plus simple à debug). Si on voulait être
  // plus tolérant on passerait en allSettled.
  const rows = await Promise.all(stubs.map(hydrateRow));
  // Le tri DESC par lastDate — Gmail trie déjà ainsi mais on s'assure.
  rows.sort((a, b) => b.lastDate - a.lastDate);

  return { rows, nextPageToken: list.nextPageToken ?? null };
}

/**
 * Token de fetch incrémenté à chaque loadInbox — permet aux fetches
 * en arrière-plan d'avorter si l'user a entretemps changé de filtre.
 */
let fetchToken = 0;

/**
 * Refresh inbox avec pattern stale-while-revalidate :
 *  1. Hydrate gmailState depuis le cache IDB instantanément (si dispo)
 *  2. Si cache frais (< 60s) → on s'arrête, pas de fetch réseau
 *  3. Sinon → fetch réseau en arrière-plan + auto-paginate jusqu'au cap
 *  4. Update + re-cache au fur et à mesure
 *
 * Le cache est par filtre (labelIds + query) pour que basculer entre
 * "Inbox" et un split `[NaoMail]/FYI` reste instantané.
 */
export async function loadInbox(): Promise<void> {
  // Snapshot du filtre courant — référence stable pour ce loadInbox
  const labelIds = resolveLabelIds();
  const query = gmailState.filter.query;
  const myToken = ++fetchToken;

  gmailState.error = null;
  gmailState.needsReauth = false;

  // 1. Hydrate du cache IDB (instantané, pas de skeleton)
  const cached = await loadCache(labelIds, query);
  // Garde-fou : l'user peut avoir changé de filtre pendant le `await`
  if (myToken !== fetchToken) return;

  if (cached) {
    gmailState.rows = cached.rows;
    gmailState.nextPageToken = cached.nextPageToken;
    gmailState.lastSyncAt = cached.cachedAt;
    void setBadge(cached.rows.filter((r) => r.unread).length);
    // Si cache frais, on ne fetch pas — gain quota, gain latence
    if (Date.now() - cached.cachedAt < CACHE_FRESH_MS) return;
  } else {
    // Pas de cache : on vide les rows pour montrer le skeleton de chargement
    gmailState.rows = [];
    gmailState.nextPageToken = null;
  }

  // 2. Fetch réseau. Si on a un cache, ce fetch est silencieux (pas de
  //    flag `loading`) ; sinon on flag pour montrer le skeleton.
  const hadCache = !!cached;
  if (!hadCache) gmailState.loading = true;

  try {
    const { rows, nextPageToken } = await fetchAndHydrate({});
    if (myToken !== fetchToken) return; // filtre changé pendant fetch
    gmailState.rows = rows;
    gmailState.nextPageToken = nextPageToken;
    gmailState.lastSyncAt = Date.now();
    void setBadge(rows.filter((r) => r.unread).length);
    void saveCache(labelIds, query, rows, nextPageToken);
  } catch (e: unknown) {
    if (e instanceof GmailScopeError) {
      gmailState.needsReauth = true;
      gmailState.error = "Scope Gmail manquant — re-sign-in";
    } else {
      gmailState.error = e instanceof Error ? e.message : String(e);
    }
  } finally {
    gmailState.loading = false;
  }

  // 3. Auto-paginate en arrière-plan jusqu'au cap. Non bloquant — la 1ère
  //    page est déjà visible.
  void autoPaginateToCap(myToken, labelIds, query);
}

/**
 * Charge des pages supplémentaires en boucle jusqu'à atteindre le cap
 * configuré (200 pour INBOX, 100 pour les autres). S'arrête si l'user a
 * changé de filtre entretemps (token mismatch) ou si plus rien à fetcher.
 */
async function autoPaginateToCap(
  myToken: number,
  labelIds: string[],
  query: string | null,
): Promise<void> {
  const cap = labelIds.includes("INBOX") ? AUTO_LOAD_CAP_INBOX : AUTO_LOAD_CAP_OTHER;
  while (
    myToken === fetchToken &&
    gmailState.nextPageToken !== null &&
    gmailState.rows.length < cap &&
    !gmailState.loadingMore
  ) {
    try {
      gmailState.loadingMore = true;
      const { rows: more, nextPageToken } = await fetchAndHydrate({
        pageToken: gmailState.nextPageToken,
      });
      if (myToken !== fetchToken) return;
      // Dédup au cas où Gmail retourne un thread sur deux pages (rare mais possible)
      const seen = new Set(gmailState.rows.map((r) => r.thread.id));
      const fresh = more.filter((r) => !seen.has(r.thread.id));
      gmailState.rows = [...gmailState.rows, ...fresh];
      gmailState.nextPageToken = nextPageToken;
    } catch (e) {
      // Erreur → on arrête l'auto-load. Le bouton manuel "Plus de fils"
      // restera dispo si nextPageToken existe.
      console.warn("autoPaginate failed:", e);
      break;
    } finally {
      gmailState.loadingMore = false;
    }
  }
  // Save final du cache après auto-pagination complète (ou interrompue)
  if (myToken === fetchToken) {
    void saveCache(labelIds, query, gmailState.rows, gmailState.nextPageToken);
  }
}

/**
 * Charge un thread complet (format=FULL) pour le ThreadView. Utilisé quand
 * l'user tap une row dans l'InboxView. Stockage dans gmailState.currentThread.
 *
 * Pas de cache cross-thread en Phase 4b : chaque tap = 1 GET. Volume faible
 * (l'user n'ouvre pas 50 threads/min). Phase 4c ajoutera Dexie pour le cache.
 */
export async function loadThread(threadId: string): Promise<void> {
  if (gmailState.loadingThread) return;
  // Si c'est le même thread que déjà loadé, no-op (évite double fetch sur re-render)
  if (gmailState.currentThread?.id === threadId) return;

  gmailState.loadingThread = true;
  gmailState.threadError = null;
  gmailState.currentThread = null;
  try {
    const thread = await getThread(threadId, { format: "full" });
    // Garde-fou : si l'user a navigué ailleurs entre-temps, ne pas écraser
    if (gmailState.selectedThreadId === threadId) {
      gmailState.currentThread = thread;
    }
    // Auto-mark-read au mount (comportement Apple Mail) — sans attendre
    // l'API. Optimistic update via markRead, qui revert si Gmail bronche.
    if (thread.messages.some((m) => m.labelIds?.includes("UNREAD"))) {
      void markRead(threadId);
    }
  } catch (e: unknown) {
    if (e instanceof GmailScopeError) {
      gmailState.needsReauth = true;
      gmailState.threadError = "Scope Gmail manquant — re-sign-in";
    } else {
      gmailState.threadError = e instanceof Error ? e.message : String(e);
    }
  } finally {
    gmailState.loadingThread = false;
  }
}

// ─── Mutations (Phase B + E) ────────────────────────────────────────────────
//
// Optimistic update : on mute gmailState immédiatement, puis on call l'API
// Gmail. En cas d'échec, on revert + toast erreur. La page reste réactive
// même sur des connexions latentes.

/** Helper : retrouve la row par threadId. Renvoie {row, idx} ou null. */
function findRow(threadId: string): { row: InboxRow; idx: number } | null {
  const idx = gmailState.rows.findIndex((r) => r.thread.id === threadId);
  if (idx < 0) return null;
  return { row: gmailState.rows[idx], idx };
}

/**
 * Persiste l'état rows courant dans le cache IDB pour le filtre actuel.
 * À appeler après chaque mutation optimistic (archive, mark read, etc.)
 * pour que la prochaine ouverture/refresh montre l'état le plus récent.
 */
function persistCurrentCache(): void {
  void saveCache(
    resolveLabelIds(),
    gmailState.filter.query,
    gmailState.rows,
    gmailState.nextPageToken,
  );
}

/**
 * Helper d'erreur partagé : transforme un GmailScopeError en flag global
 * needsReauth (la bannière "Re-connecter Gmail" s'affiche dans InboxView)
 * + toast informatif. Les autres erreurs deviennent juste un toast.
 */
function reportMutationError(e: unknown, fallbackLabel: string): void {
  if (e instanceof GmailScopeError) {
    gmailState.needsReauth = true;
    showToast({
      message: "Permission Gmail manquante — re-sign-in nécessaire",
    });
    return;
  }
  showToast({
    message: `Erreur ${fallbackLabel} : ${e instanceof Error ? e.message : "?"}`,
  });
}

/** Mark un thread lu (remove UNREAD label). Optimistic + revert. */
export async function markRead(threadId: string): Promise<void> {
  const found = findRow(threadId);
  const wasUnread = found?.row.unread ?? null;
  if (found && wasUnread) {
    gmailState.rows[found.idx] = { ...found.row, unread: false };
  }
  // Si on a un thread ouvert, update aussi ses labels (visuel local)
  const cur = gmailState.currentThread;
  if (cur && cur.id === threadId) {
    gmailState.currentThread = {
      ...cur,
      messages: cur.messages.map((m) => ({
        ...m,
        labelIds: (m.labelIds ?? []).filter((l) => l !== "UNREAD"),
      })),
    };
  }
  try {
    await modifyThread(threadId, { removeLabelIds: ["UNREAD"] });
    persistCurrentCache();
  } catch (e) {
    if (found && wasUnread) {
      gmailState.rows[found.idx] = { ...gmailState.rows[found.idx], unread: true };
    }
    reportMutationError(e, "marquage lu");
  }
}

/** Mark un thread non-lu (add UNREAD label). Optimistic + revert. */
export async function markUnread(threadId: string): Promise<void> {
  const found = findRow(threadId);
  const wasUnread = found?.row.unread ?? null;
  if (found && !wasUnread) {
    gmailState.rows[found.idx] = { ...found.row, unread: true };
  }
  try {
    await modifyThread(threadId, { addLabelIds: ["UNREAD"] });
    persistCurrentCache();
  } catch (e) {
    if (found && wasUnread === false) {
      gmailState.rows[found.idx] = { ...gmailState.rows[found.idx], unread: false };
    }
    reportMutationError(e, "marquage non-lu");
  }
}

/**
 * Pose ou retire un label sur un fil. Optimiste sur les messages en mémoire
 * (les chips de la liste se mettent à jour tout de suite), revert en cas
 * d'échec, comme les autres mutations.
 */
export async function toggleThreadLabel(
  threadId: string,
  labelId: string,
  add: boolean,
): Promise<void> {
  const found = findRow(threadId);
  const patch = (msgs: GmailMessage[]) =>
    msgs.map((m) => ({
      ...m,
      labelIds: add
        ? [...new Set([...(m.labelIds ?? []), labelId])]
        : (m.labelIds ?? []).filter((l) => l !== labelId),
    }));

  if (found) {
    gmailState.rows[found.idx] = {
      ...found.row,
      thread: { ...found.row.thread, messages: patch(found.row.thread.messages) },
    };
  }
  const cur = gmailState.currentThread;
  if (cur && cur.id === threadId) {
    gmailState.currentThread = { ...cur, messages: patch(cur.messages) };
  }

  try {
    await modifyThread(
      threadId,
      add ? { addLabelIds: [labelId] } : { removeLabelIds: [labelId] },
    );
    persistCurrentCache();
  } catch (e) {
    // Revert : on rejoue l'inverse sur ce qu'on vient d'écrire.
    const undo = (msgs: GmailMessage[]) =>
      msgs.map((m) => ({
        ...m,
        labelIds: add
          ? (m.labelIds ?? []).filter((l) => l !== labelId)
          : [...new Set([...(m.labelIds ?? []), labelId])],
      }));
    const again = findRow(threadId);
    if (again) {
      gmailState.rows[again.idx] = {
        ...again.row,
        thread: { ...again.row.thread, messages: undo(again.row.thread.messages) },
      };
    }
    const cur2 = gmailState.currentThread;
    if (cur2 && cur2.id === threadId) {
      gmailState.currentThread = { ...cur2, messages: undo(cur2.messages) };
    }
    reportMutationError(e, "modification de label");
  }
}

/**
 * Archive un thread (remove INBOX). Optimistic remove de la row + toast
 * "Archivé" avec action Annuler 4 s. Si l'user undo : re-add INBOX.
 */
export async function archiveThread(threadId: string): Promise<void> {
  const found = findRow(threadId);
  if (!found) return;
  const { row, idx } = found;
  // Remove optimistic
  gmailState.rows = gmailState.rows.filter((r) => r.thread.id !== threadId);
  try {
    await modifyThread(threadId, { removeLabelIds: ["INBOX"] });
    persistCurrentCache();
    showToast({
      message: "Archivé",
      action: {
        label: "Annuler",
        run: () => void unarchive(row, idx),
      },
    });
  } catch (e) {
    // Re-insert à sa position d'origine
    const restored = [...gmailState.rows];
    restored.splice(Math.min(idx, restored.length), 0, row);
    gmailState.rows = restored;
    reportMutationError(e, "archive");
  }
}

/** Re-add INBOX + reinsère la row à sa position d'origine. */
async function unarchive(row: InboxRow, originalIdx: number): Promise<void> {
  // Re-insert visuel — peut décaler si d'autres rows sont apparues entretemps
  if (!gmailState.rows.some((r) => r.thread.id === row.thread.id)) {
    const next = [...gmailState.rows];
    next.splice(Math.min(originalIdx, next.length), 0, row);
    gmailState.rows = next;
  }
  try {
    await modifyThread(row.thread.id, { addLabelIds: ["INBOX"] });
    persistCurrentCache();
  } catch (e) {
    showToast({ message: `Undo échoué : ${e instanceof Error ? e.message : "?"}` });
  }
}

/** Append la page suivante. No-op si pas de nextPageToken. */
export async function loadMore(): Promise<void> {
  if (gmailState.loadingMore || !gmailState.nextPageToken) return;
  gmailState.loadingMore = true;
  try {
    const { rows, nextPageToken } = await fetchAndHydrate({
      pageToken: gmailState.nextPageToken,
    });
    // Dédup au cas où — un thread peut apparaître 2 fois si l'inbox a bougé
    const seen = new Set(gmailState.rows.map((r) => r.thread.id));
    const fresh = rows.filter((r) => !seen.has(r.thread.id));
    gmailState.rows = [...gmailState.rows, ...fresh];
    gmailState.nextPageToken = nextPageToken;
    persistCurrentCache();
  } catch (e: unknown) {
    if (e instanceof GmailScopeError) {
      gmailState.needsReauth = true;
    } else {
      gmailState.error = e instanceof Error ? e.message : String(e);
    }
  } finally {
    gmailState.loadingMore = false;
  }
}
